import React, { useState, useEffect } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import { Business, TrendingUp, Person, Article, Schedule } from "@mui/icons-material";

import { ButtonFL } from "../ui/ButtonFL";
import { apiGetFranchise } from "../../services/apiGetFranchise";

export const FranchiseDetailsModal = ({ isOpen, onClose, communityId, franchiseId }) => {
  const [franchise, setFranchise] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen && franchiseId) {
      loadFranchiseDetails();
    }
  }, [isOpen, franchiseId]);

  const loadFranchiseDetails = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await apiGetFranchise(communityId, franchiseId);
      setFranchise(data);
    } catch (err) {
      setError("Erro ao carregar detalhes da unidade. Tente novamente.");
      console.error("Error loading franchise details:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFranchise(null);
    setError("");
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "APROVADO":
        return "success";
      case "PENDENTE":
        return "warning";
      case "REPROVADO":
        return "error";
      default:
        return "default";
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Business sx={{ color: "var(--primary-color)" }} />
          <Typography variant="h6" component="div">
            Detalhes da Unidade
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {franchise && !loading && (
          <Box sx={{ mt: 1 }}>
            <Card sx={{ mb: 3, bgcolor: "var(--grey-50-color)" }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 1, color: "var(--primary-color)" }}>
                  {franchise.name}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    ID: {franchise.id}
                  </Typography>
                  <Chip
                    label={franchise.isActive ? "Ativa" : "Inativa"}
                    color={franchise.isActive ? "success" : "error"}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Criada em: {new Date(franchise.created_at).toLocaleDateString("pt-BR")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Atualizada em: {new Date(franchise.updated_at).toLocaleDateString("pt-BR")}
                </Typography>
              </CardContent>
            </Card>

            <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
              <TrendingUp sx={{ color: "var(--primary-color)" }} />
              Estatísticas
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Person sx={{ fontSize: 32, color: "var(--primary-color)", mb: 1 }} />
                    <Typography variant="h4" color="primary">
                      {franchise.statistics?.creatorsCount || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Membros
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Article sx={{ fontSize: 32, color: "var(--success-color)", mb: 1 }} />
                    <Typography variant="h4" color="success.main">
                      {franchise.statistics?.totalPosts || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total de Posts
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {franchise.statistics?.postsByStatus && Object.keys(franchise.statistics.postsByStatus).length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Posts por Status
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(franchise.statistics.postsByStatus).map(([status, count]) => (
                    <Grid item xs={12} sm={6} md={4} key={status}>
                      <Card>
                        <CardContent>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box>
                              <Typography variant="h5">
                                {count}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {status}
                              </Typography>
                            </Box>
                            <Chip
                              label={status}
                              color={getStatusColor(status)}
                              size="small"
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <ButtonFL
          onClick={handleClose}
          bgcolor="var(--grey-100-color)"
          textColor="var(--primary-text-color)"
        >
          Fechar
        </ButtonFL>
      </DialogActions>
    </Dialog>
  );
};
